import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { T } from './designTokens';
import { ClerkProvider, useAuth, useUser } from '@clerk/react';
import { setAuthProvider, trackLinkedInSignup } from './utils/api';
import { maybeFireSignupConversion, maybeFireLogin } from './utils/signupConversion';
import { hasConsent } from '@privisy/ui-bridge';
import PrivacyChoicesLink from './components/PrivacyChoicesLink';
import { makeClerkRouter } from './utils/clerkRouter';

const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const isClerkConfigured =
  Boolean(publishableKey) &&
  /^pk_(test|live)_[A-Za-z0-9]{10,}/.test(publishableKey ?? '');

if (!isClerkConfigured) {
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:${T.gray900};font-family:${T.fontBody};color:${T.gray100};padding:40px">
        <div style="max-width:480px;text-align:center">
          <div style="font-size:32px;margin-bottom:16px">🔑</div>
          <h1 style="font-size:20px;font-weight:700;margin:0 0 12px;color:${T.gray50}">Clerk key required</h1>
          <p style="font-size:14px;color:${T.gray400};line-height:1.6;margin:0 0 20px">
            Set <code style="background:${T.gray800};padding:2px 6px;border-radius:4px;color:${T.cobalt400}">REACT_APP_CLERK_PUBLISHABLE_KEY</code>
            in <code style="background:${T.gray800};padding:2px 6px;border-radius:4px;color:${T.cobalt400}">packages/frontend/.env</code>
            to a Clerk development key (<code style="background:${T.gray800};padding:2px 6px;border-radius:4px;color:${T.cobalt400}">pk_test_...</code>).
          </p>
          <p style="font-size:13px;color:${T.gray500};margin:0">
            Get your keys at <a href="https://dashboard.clerk.com/~/api-keys" target="_blank" rel="noreferrer" style="color:${T.cobalt400}">dashboard.clerk.com</a>
          </p>
        </div>
      </div>`;
  }
  throw new Error('[auth] REACT_APP_CLERK_PUBLISHABLE_KEY is not set. See packages/frontend/.env.example.');
}

function AppWithClerk() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  useEffect(() => { setAuthProvider(getToken); }, [getToken]);

  // Signup vs login analytics (GA4/Vercel only — the Google Ads conversion is
  // owned server-side by the Clerk user.created webhook). A genuine sign-up is
  // signalled by the `welcome` marker that <SignUpButton forceRedirectUrl>
  // lands on; everything else signed-in is treated as a returning login.
  useEffect(() => {
    if (!isSignedIn || !user?.id) return;
    const email = user.primaryEmailAddress?.emailAddress;
    void maybeFireSignupConversion({
      userId: user.id,
      email,
      onFired: (userId, firedEmail) => {
        if (firedEmail && hasConsent()) {
          trackLinkedInSignup(firedEmail, userId).catch(() => { /* non-blocking */ });
        }
      },
    }).then((fired) => {
      if (!fired) maybeFireLogin(user.id);
    });
  }, [isSignedIn, user, user?.id]);

  if (!isLoaded) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.gray900,
        color: T.gray100,
        fontFamily: T.fontBody
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            margin: '0 auto 20px',
            border: `3px solid ${T.overlayLine}`,
            borderTopColor: T.cobalt400,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: '14px', fontWeight: 500, letterSpacing: '0.02em' }}>Initializing Privisy...</div>
        </div>
      </div>
    );
  }

  return <App authEnabled={true} getToken={getToken} clerkSignedIn={!!isSignedIn} isAdmin={user?.publicMetadata?.role === 'admin'} />;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ClerkProvider publishableKey={publishableKey as string} afterSignOutUrl="/" {...makeClerkRouter()}>
      {/* No consent banner: tracking is opt-out (CCPA/CPRA). The opt-out
          control lives at getprivisy.com/your-privacy-choices, reachable from
          every route via PrivacyChoicesLink, and GPC is honored automatically. */}
      <AppWithClerk />
      <PrivacyChoicesLink />
    </ClerkProvider>
  </React.StrictMode>
);
